import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { createPortal } from 'react-dom';
import { Sparkles, Plus, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Loading from '../utils/Loading';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const SummaryTemplates = () => {
  const API = useContext(ApiContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prompt: '',
    category: 'autre',
    isDefault: false,
  });

  const categories = [
    { id: 'presentation', label: 'Présentation' },
    { id: 'qualification', label: 'Qualification' },
    { id: 'suivi', label: 'Suivi' },
    { id: 'autre', label: 'Autre' },
  ];

  // Charger les templates
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/summary-templates');
      if (response.data.success) {
        setTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Erreur lors du chargement des templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Ouvrir la modale pour créer un template
  const handleCreateNew = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      prompt: '',
      category: 'autre',
      isDefault: false,
    });
    setShowModal(true);
  };

  // Ouvrir la modale pour modifier un template
  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      prompt: template.prompt,
      category: template.category,
      isDefault: template.isDefault,
    });
    setShowModal(true);
  };

  // Sauvegarder (créer ou modifier)
  const handleSave = async () => {
    if (!formData.name || !formData.description || !formData.prompt) {
      toast.warning('Tous les champs sont obligatoires');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        // Modification
        await API.put(`/api/summary-templates/${editingTemplate._id}`, formData);
        toast.success('Template modifié avec succès');
      } else {
        // Création
        await API.post('/api/summary-templates', formData);
        toast.success('Template créé avec succès');
      }
      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Supprimer un template
  const handleDelete = async () => {
    if (!templateToDelete) return;

    const id = templateToDelete;
    setShowDeleteDialog(false);
    setTemplateToDelete(null);

    try {
      await API.delete(`/api/summary-templates/${id}`);
      toast.success('Template supprimé avec succès');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 m-0">Templates de résumé IA</h2>
          <p className="text-sm text-gray-600 mt-1">
            Créez des templates pour générer des résumés professionnels avec l'IA
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
        >
          <Plus size={20} />
          Nouveau template
        </button>
      </div>

      {/* Liste des templates */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loading />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Sparkles size={48} className="mx-auto text-violet-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun template pour le moment
          </h3>
          <p className="text-gray-600 mb-6">
            Créez votre premier template pour commencer à générer des résumés IA
          </p>
          <button
            onClick={handleCreateNew}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors mt-6"
          >
            <Plus size={20} />
            Créer un template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((template) => (
            <div
              key={template._id}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:border-violet-300 transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900 m-0">{template.name}</h3>
                    {template.isDefault && (
                      <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                        Par défaut
                      </span>
                    )}
                  </div>
                  <span className="inline-block text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full capitalize mb-2">
                    {categories.find((c) => c.id === template.category)?.label || template.category}
                  </span>
                  <p className="text-sm text-gray-600">{template.description}</p>
                </div>
                <Sparkles size={24} className="text-violet-400 ml-2 flex-shrink-0" />
              </div>

              <div className="bg-gray-50 rounded p-3 mb-3">
                <p className="text-xs font-medium text-gray-700 mb-1">Prompt IA :</p>
                <p className="text-xs text-gray-600 line-clamp-2">{template.prompt}</p>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => handleEdit(template)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                >
                  <Edit size={16} />
                  Modifier
                </button>
                <button
                  onClick={() => {
                    setTemplateToDelete(template._id);
                    setShowDeleteDialog(true);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 size={16} />
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modale de création/édition */}
      {showModal && createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[99999] p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 m-0">
                  {editingTemplate ? 'Modifier le template' : 'Nouveau template'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Formulaire */}
              <div className="space-y-4">
                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du template *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    placeholder="Ex: Résumé de qualification"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    placeholder="Ex: Génère un résumé structuré de la qualification du candidat"
                  />
                </div>

                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Prompt IA */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt pour l'IA *
                  </label>
                  <textarea
                    value={formData.prompt}
                    onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    placeholder="Ex: Crée un résumé structuré en 3 parties : profil, compétences clés, et points d'attention..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ce prompt sera utilisé par GPT-4o pour générer le résumé
                  </p>
                </div>

                {/* Par défaut */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isDefault"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
                    Définir comme template par défaut
                  </label>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loading />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      {editingTemplate ? 'Modifier' : 'Créer'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Supprimer le template"
        message="Êtes-vous sûr de vouloir supprimer ce template ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
};

export default SummaryTemplates;
